import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowRight, RotateCcw } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { UnitToggle } from "@/components/calculator/UnitToggle";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { formatBDT } from "@/lib/bdCurrency";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import { solveSimple, simpleSchedule, type SolveFor } from "@/lib/calculators/interest";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Interest Calculator", bn: "সুদ ক্যালকুলেটর" },
  desc: {
    en: "Calculate simple interest in Bangladeshi Taka. Solve for interest, principal, rate, or time. For chakrabriddhi (compound) interest, use the dedicated compound interest calculator.",
    bn: "বাংলাদেশী টাকায় সরল সুদ হিসাব করুন। সুদ, মূলধন, হার বা সময় — যেকোনো একটি নির্ণয় করুন। চক্রবৃদ্ধি সুদের জন্য আলাদা ক্যালকুলেটর ব্যবহার করুন।",
  },
  type: { en: "Interest type", bn: "সুদের ধরন" },
  simple: { en: "Simple", bn: "সরল" },
  compound: { en: "Compound →", bn: "চক্রবৃদ্ধি →" },
  solveFor: { en: "What to calculate", bn: "কী বের করতে চান" },

  interest: { en: "Interest amount", bn: "সুদ" },
  principal: { en: "Principal amount", bn: "মূলধন" },
  rate: { en: "Annual interest rate", bn: "বার্ষিক সুদের হার" },
  time: { en: "Time period", bn: "সময়কাল" },
  total: { en: "Total amount", bn: "মোট পরিমাণ" },

  years: { en: "Years", bn: "বছর" },
  months: { en: "Months", bn: "মাস" },
  reset: { en: "Reset", bn: "রিসেট" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },

  scheduleTitle: { en: "Year-by-year accumulation", bn: "বছরভিত্তিক জমা" },
  year: { en: "Year", bn: "বছর" },
  interestYr: { en: "Interest this year", bn: "এই বছরের সুদ" },
  cumulative: { en: "Cumulative interest", bn: "মোট সুদ" },
  balance: { en: "Balance", bn: "ব্যালেন্স" },

  invalid: {
    en: "Please enter positive values for all required fields.",
    bn: "সব প্রয়োজনীয় ঘরে শূন্যের চেয়ে বড় মান দিন।",
  },
} as const;

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How is simple interest calculated?", bn: "সরল সুদ কীভাবে গণনা করা হয়?" },
    a: {
      en: "Simple interest = (Principal × Rate × Time) ÷ 100. The interest is always calculated on the original principal — it does not compound. Example: ৳1,00,000 at 10% for 5 years → SI = ৳50,000.",
      bn: "সরল সুদ = (মূলধন × হার × সময়) ÷ ১০০। সুদ সবসময় মূল মূলধনের উপর হিসাব হয় — চক্রবৃদ্ধি হয় না। উদাহরণ: ৳১,০০,০০০ ১০% হারে ৫ বছরে → সুদ = ৳৫০,০০০।",
    },
  },
  {
    q: { en: "What's the difference between simple and compound interest?", bn: "সরল ও চক্রবৃদ্ধি সুদের পার্থক্য কী?" },
    a: {
      en: "Simple interest is calculated only on the original principal. Compound interest adds previously earned interest to the principal each period, so future interest is calculated on a larger base. Over long periods compound grows much faster.",
      bn: "সরল সুদ শুধু মূল মূলধনের উপর হিসাব হয়। চক্রবৃদ্ধি সুদ প্রতিটি সময়ের অর্জিত সুদ মূলধনে যোগ হয়, তাই পরবর্তী সুদ বড় ভিত্তির উপর হয়। দীর্ঘমেয়াদে চক্রবৃদ্ধি অনেক বেশি বাড়ে।",
    },
  },
  {
    q: { en: "When do BD banks use simple vs compound interest?", bn: "বিডি ব্যাংক কখন সরল আর কখন চক্রবৃদ্ধি ব্যবহার করে?" },
    a: {
      en: "Most savings products in Bangladesh — DPS, FDR, Sanchayapatra — use compound interest. Simple interest is more common in short-term loans, microfinance instalments, and informal lending where the term is under a year.",
      bn: "বাংলাদেশের অধিকাংশ সঞ্চয় পণ্য — ডিপিএস, এফডিআর, সঞ্চয়পত্র — চক্রবৃদ্ধি সুদ ব্যবহার করে। স্বল্পমেয়াদী ঋণ, মাইক্রোফাইন্যান্স কিস্তি ও অনানুষ্ঠানিক ঋণে সরল সুদ বেশি ব্যবহৃত হয়।",
    },
  },
  {
    q: { en: "How do I find the interest rate if I know everything else?", bn: "অন্য সব জানলে সুদের হার কীভাবে বের করব?" },
    a: {
      en: "Use the inverse formula R = (I × 100) ÷ (P × T). Example: P = ৳50,000, I = ৳15,000, T = 3 years → R = (15000 × 100) ÷ (50000 × 3) = 10%. Switch the 'What to calculate' selector above to 'Rate' to do this automatically.",
      bn: "বিপরীত সূত্র R = (I × ১০০) ÷ (P × T)। উদাহরণ: P = ৳৫০,০০০, I = ৳১৫,০০০, T = ৩ বছর → R = (১৫০০০ × ১০০) ÷ (৫০০০০ × ৩) = ১০%। উপরে 'কী বের করতে চান' থেকে 'হার' নির্বাচন করলে এটি স্বয়ংক্রিয়ভাবে হয়।",
    },
  },
  {
    q: { en: "Is interest income taxable in Bangladesh?", bn: "বাংলাদেশে সুদ আয় করযোগ্য?" },
    a: {
      en: "Yes. Banks deduct TDS (Tax Deducted at Source) on interest credited to your account — typically 10% if you have a TIN, 15% if you don't. Sanchayapatra interest above ৳5 lakh/year is also subject to 10% TDS.",
      bn: "হ্যাঁ। ব্যাংক আপনার সুদে TDS (উৎসে কর) কাটে — TIN থাকলে সাধারণত ১০%, না থাকলে ১৫%। সঞ্চয়পত্রের ৫ লক্ষ টাকার বেশি সুদেও ১০% TDS।",
    },
  },
  {
    q: { en: "সুদ কীভাবে গণনা করা হয়?", bn: "সুদ কীভাবে গণনা করা হয়?" },
    a: {
      en: "সরল সুদ: I = (P × R × T) / ১০০। চক্রবৃদ্ধি সুদ: A = P × (১ + r/n)^(n × t)। উপরের ক্যালকুলেটরে মান বসিয়ে তাৎক্ষণিক ফলাফল দেখুন।",
      bn: "সরল সুদ: I = (P × R × T) / ১০০। চক্রবৃদ্ধি সুদ: A = P × (১ + r/n)^(n × t)। উপরের ক্যালকুলেটরে মান বসিয়ে তাৎক্ষণিক ফলাফল দেখুন।",
    },
  },
];

export const Route = createFileRoute("/interest-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Interest Calculator — Simple & Compound",
      description:
        "Free simple interest calculator. Calculate interest amount, principal, rate, or time. Bangladesh-friendly with BDT currency. সুদ ক্যালকুলেটর।",
      path: "/interest-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Interest Calculator", path: "/interest-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: InterestCalculatorPage,
});

const PRINCIPAL_MAX = 100_000_000;
const INTEREST_MAX = 100_000_000;
const RATE_MAX = 50;
const YEARS_MAX = 50;

function InterestCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));

  const [solveFor, setSolveFor] = useState<SolveFor>("interest");
  const [principalStr, setPrincipalStr] = useState("100000");
  const [rateStr, setRateStr] = useState("10");
  const [timeUnit, setTimeUnit] = useState<"years" | "months">("years");
  const [timeStr, setTimeStr] = useState("5");
  const [interestStr, setInterestStr] = useState("50000");
  const [bnDigits, setBnDigits] = useState(false);

  const principal = clamp(parseFloat(principalStr) || 0, 0, PRINCIPAL_MAX);
  const rate = clamp(parseFloat(rateStr) || 0, 0, RATE_MAX);
  const timeVal = Math.max(0, parseFloat(timeStr) || 0);
  const years = timeUnit === "years" ? timeVal : timeVal / 12;
  const interestIn = clamp(parseFloat(interestStr) || 0, 0, INTEREST_MAX);

  const result = useMemo(
    () =>
      solveSimple(solveFor, {
        principal,
        annualRatePct: rate,
        years,
        interest: interestIn,
      }),
    [solveFor, principal, rate, years, interestIn],
  );

  const schedule = useMemo(() => {
    if (!result || result.years <= 0) return [];
    return simpleSchedule(result.principal, result.annualRatePct, result.years);
  }, [result]);

  const money = (v: number, frac = 0) => {
    const useBn = lang === "bn" || bnDigits;
    return formatBDT(Math.round(v * 100) / 100, useBn ? "bn" : "en", frac);
  };

  const reset = () => {
    setSolveFor("interest");
    setPrincipalStr("100000");
    setRateStr("10");
    setTimeUnit("years");
    setTimeStr("5");
    setInterestStr("50000");
  };

  const SOLVE_OPTIONS: { value: SolveFor; label: string }[] = [
    { value: "interest", label: tr("interest") },
    { value: "principal", label: tr("principal") },
    { value: "rate", label: tr("rate") },
    { value: "time", label: tr("time") },
  ];

  // For each input, hide it when it's the unknown
  const showPrincipal = solveFor !== "principal";
  const showRate = solveFor !== "rate";
  const showTime = solveFor !== "time";
  const showInterest = solveFor !== "interest";

  // Display values for any computed unknown
  const computedPrincipal = solveFor === "principal" ? result?.principal : null;
  const computedRate = solveFor === "rate" ? result?.annualRatePct : null;
  const computedYears = solveFor === "time" ? result?.years : null;
  const computedInterest = solveFor === "interest" ? result?.interest : null;

  return (
    <CalculatorLayout
      slug="interest-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Percent"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Type toggle */}
      <div className="space-y-2">
        <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("type")}</p>
        <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
          <button
            type="button"
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition",
              "bg-card text-foreground shadow-sm",
              lang === "bn" && "font-bn",
            )}
          >
            {tr("simple")}
          </button>
          <Link
            to="/compound-interest-calculator"
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition hover:text-foreground",
              lang === "bn" && "font-bn",
            )}
          >
            {tr("compound")}
          </Link>
        </div>
      </div>

      {/* Solve-for chips */}
      <div className="mt-5 space-y-2">
        <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("solveFor")}</p>
        <div className="flex flex-wrap gap-1.5">
          {SOLVE_OPTIONS.map((o) => {
            const active = solveFor === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => setSolveFor(o.value)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                  active
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-card text-foreground/80 hover:border-accent/40 hover:text-foreground",
                  lang === "bn" && "font-bn",
                )}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Principal */}
      {showPrincipal && (
        <div className="mt-6 space-y-3">
          <NumberInput label={tr("principal")} value={principalStr} onChange={setPrincipalStr} unit="৳" />
          <Slider
            value={[principal]}
            min={0}
            max={PRINCIPAL_MAX}
            step={1000}
            onValueChange={(v) => setPrincipalStr(String(v[0]))}
          />
        </div>
      )}

      {/* Rate */}
      {showRate && (
        <div className="mt-6 space-y-3">
          <NumberInput label={tr("rate")} value={rateStr} onChange={setRateStr} unit="%" />
          <Slider
            value={[rate]}
            min={0}
            max={RATE_MAX}
            step={0.1}
            onValueChange={(v) => setRateStr(String(v[0]))}
          />
        </div>
      )}

      {/* Time */}
      {showTime && (
        <div className="mt-6 grid gap-3 md:grid-cols-[1fr_auto]">
          <div>
            <NumberInput
              label={tr("time")}
              value={timeStr}
              onChange={setTimeStr}
              unit={timeUnit === "years" ? tr("years") : tr("months")}
            />
            <Slider
              className="mt-3"
              value={[timeVal]}
              min={1}
              max={timeUnit === "years" ? YEARS_MAX : YEARS_MAX * 12}
              step={1}
              onValueChange={(v) => setTimeStr(String(v[0]))}
            />
          </div>
          <div className="self-end">
            <UnitToggle<"years" | "months">
              value={timeUnit}
              onChange={(u) => {
                if (u === timeUnit) return;
                if (u === "months") {
                  const m = Math.round(timeVal * 12);
                  setTimeUnit("months");
                  setTimeStr(String(m));
                } else {
                  const y = Math.max(1, Math.round((timeVal / 12) * 10) / 10);
                  setTimeUnit("years");
                  setTimeStr(String(y));
                }
              }}
              options={[
                { value: "years", label: tr("years") },
                { value: "months", label: tr("months") },
              ]}
            />
          </div>
        </div>
      )}

      {/* Interest (input when solving for something else) */}
      {showInterest && (
        <div className="mt-6 space-y-3">
          <NumberInput label={tr("interest")} value={interestStr} onChange={setInterestStr} unit="৳" />
          <Slider
            value={[interestIn]}
            min={0}
            max={INTEREST_MAX}
            step={500}
            onValueChange={(v) => setInterestStr(String(v[0]))}
          />
        </div>
      )}

      <div className="mt-6 flex items-center justify-between gap-3">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input
            type="checkbox"
            checked={bnDigits}
            onChange={(e) => setBnDigits(e.target.checked)}
            className="h-3.5 w-3.5 accent-accent"
          />
          {tr("bnDigits")}
        </label>
        <Button variant="outline" onClick={reset} className="gap-2">
          <RotateCcw size={14} />
          {tr("reset")}
        </Button>
      </div>

      {!result && (
        <p className={cn("mt-6 text-sm text-yellow-600 dark:text-yellow-400", lang === "bn" && "font-bn")}>
          ⚠️ {tr("invalid")}
        </p>
      )}

      {result && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 space-y-6"
        >
          {/* Primary results */}
          <div className="grid gap-3 md:grid-cols-2">
            <ResultCard
              label={tr("interest")}
              value={money(result.interest)}
              accent={solveFor === "interest"}
              hint={computedInterest != null ? formatSolved(lang) : undefined}
            />
            <ResultCard label={tr("total")} value={money(result.total)} />
          </div>

          {/* Show solved unknown card if it's not interest */}
          {(computedPrincipal != null || computedRate != null || computedYears != null) && (
            <div className="grid gap-3 md:grid-cols-3">
              {computedPrincipal != null && (
                <ResultCard label={tr("principal")} value={money(computedPrincipal)} accent hint={formatSolved(lang)} />
              )}
              {computedRate != null && (
                <ResultCard
                  label={tr("rate")}
                  value={`${num(computedRate.toFixed(2))}%`}
                  accent
                  hint={formatSolved(lang)}
                />
              )}
              {computedYears != null && (
                <ResultCard
                  label={tr("time")}
                  value={`${num(computedYears.toFixed(2))} ${tr("years")}`}
                  accent
                  hint={formatSolved(lang)}
                />
              )}
            </div>
          )}

          {/* Visual bar — principal vs interest */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-2 flex justify-between text-xs text-muted-foreground">
              <span className={cn(lang === "bn" && "font-bn")}>{tr("principal")}: {money(result.principal)}</span>
              <span className={cn(lang === "bn" && "font-bn")}>{tr("interest")}: {money(result.interest)}</span>
            </div>
            <div className="flex h-3 overflow-hidden rounded-full bg-muted">
              <div
                className="bg-muted-foreground/60"
                style={{ width: `${pct(result.principal, result.total)}%` }}
              />
              <div
                className="bg-accent"
                style={{ width: `${pct(result.interest, result.total)}%` }}
              />
            </div>
          </div>

          {/* Year-by-year accumulation */}
          {schedule.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-3 md:p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("scheduleTitle")}</h3>
                <ShareButton title={`${tr("interest")}: ${money(result.interest)}`} />
              </div>
              <div className="max-h-80 overflow-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/80 text-xs uppercase tracking-wide text-muted-foreground backdrop-blur">
                    <tr>
                      <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("year")}</th>
                      <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("interestYr")}</th>
                      <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("cumulative")}</th>
                      <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("balance")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((y) => (
                      <tr key={y.year} className="border-t border-border">
                        <td className="px-3 py-2 tabular-nums">{num(y.year)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(y.interestThisYear)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(y.cumulativeInterest)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(y.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "en" ? "Simple interest formula" : "সরল সুদের সূত্র"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <strong>SI = (P × R × T) / 100</strong> &nbsp;|&nbsp; <strong>A = P + SI</strong>
            <br />
            {lang === "en"
              ? "P = principal (৳), R = annual interest rate (%), T = time in years. Interest is always calculated on the original principal — there's no compounding. Example: ৳1,00,000 at 10% for 5 years → SI = ৳50,000, total = ৳1,50,000."
              : "P = মূলধন (৳), R = বার্ষিক সুদের হার (%), T = বছরে সময়। সুদ সর্বদা মূল মূলধনের উপর হিসাব হয় — চক্রবৃদ্ধি হয় না। উদাহরণ: ৳১,০০,০০০ ১০% হারে ৫ বছরে → সুদ = ৳৫০,০০০, মোট = ৳১,৫০,০০০।"}
          </p>
          <ul className={cn("mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "P = (I × 100) / (R × T)" : "P = (I × ১০০) / (R × T)"}</li>
            <li>{lang === "en" ? "R = (I × 100) / (P × T)" : "R = (I × ১০০) / (P × T)"}</li>
            <li>{lang === "en" ? "T = (I × 100) / (P × R)" : "T = (I × ১০০) / (P × R)"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Simple vs compound interest" : "সরল বনাম চক্রবৃদ্ধি সুদ"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "With simple interest your interest is the same every year. With compound interest, each year's interest is added to the principal, so next year's interest is bigger. At ৳1,00,000 / 10% / 10 years: simple gives ৳1,00,000 interest; compound (annual) gives ৳1,59,374."
              : "সরল সুদে প্রতি বছর সুদ একই থাকে। চক্রবৃদ্ধি সুদে প্রতি বছরের সুদ মূলধনে যোগ হয়, তাই পরের বছর বেশি সুদ। ৳১,০০,০০০ / ১০% / ১০ বছর: সরল ৳১,০০,০০০ সুদ; চক্রবৃদ্ধি (বার্ষিক) ৳১,৫৯,৩৭৪।"}
          </p>
          <Link
            to="/compound-interest-calculator"
            className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-accent hover:underline"
          >
            {lang === "en" ? "Open Compound Interest Calculator" : "চক্রবৃদ্ধি সুদ ক্যালকুলেটর খুলুন"}
            <ArrowRight size={14} />
          </Link>
        </Section>

        <Section title={lang === "en" ? "Common BD use cases" : "বাংলাদেশে যেখানে ব্যবহৃত হয়"} lang={lang}>
          <ul className={cn("list-disc space-y-2 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en" ? (
              <>
                <li><strong>Short-term loans:</strong> Many BD MFIs and informal lenders quote a flat (simple) rate over months — easy to compute, but converts to a much higher effective rate.</li>
                <li><strong>NGO microfinance:</strong> BRAC, ASA, Grameen Bank weekly-instalment loans are quoted in flat-rate terms; use this calculator to see total interest cost.</li>
                <li><strong>Deposit interest accrual:</strong> Daily savings interest in BD banks is conceptually simple-interest accrued and credited periodically.</li>
                <li><strong>Bond / Sanchayapatra coupons:</strong> Profit-bearing instruments pay simple interest as periodic coupons; compounding only applies if you reinvest.</li>
              </>
            ) : (
              <>
                <li><strong>স্বল্পমেয়াদী ঋণ:</strong> অনেক বিডি MFI ও অনানুষ্ঠানিক ঋণদাতা মাসিক ফ্ল্যাট (সরল) হারে কোট করে — সহজ মনে হলেও কার্যকর হার অনেক বেশি।</li>
                <li><strong>এনজিও মাইক্রোফাইন্যান্স:</strong> BRAC, ASA, গ্রামীণ ব্যাংকের সাপ্তাহিক কিস্তির ঋণ ফ্ল্যাট-হারে দেওয়া হয়; এই ক্যালকুলেটরে মোট সুদের খরচ দেখুন।</li>
                <li><strong>আমানতের সুদ:</strong> বিডি ব্যাংকের দৈনিক সঞ্চয়ী সুদ ধারণাগতভাবে সরল-সুদ যা পর্যায়ক্রমে জমা হয়।</li>
                <li><strong>বন্ড / সঞ্চয়পত্রের কুপন:</strong> মুনাফা ভিত্তিক স্কিম পর্যায়ক্রমে সরল-সুদ হিসেবে দেয়; পুনঃবিনিয়োগ করলেই চক্রবৃদ্ধি হয়।</li>
              </>
            )}
          </ul>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

/* ---------- Helpers ---------- */

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function pct(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.max(0, Math.min(100, (part / whole) * 100));
}

function formatSolved(lang: "en" | "bn"): string {
  return lang === "en" ? "Solved" : "নির্ণীত";
}

function ResultCard({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: string;
  accent?: boolean;
  hint?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "rounded-xl border p-4",
        accent ? "border-accent/40 bg-accent/5" : "border-border bg-card",
      )}
    >
      <p className={cn("text-xs font-semibold uppercase tracking-wide", accent ? "text-accent" : "text-muted-foreground")}>
        {label}
        {hint && <span className="ml-2 rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium normal-case text-accent">{hint}</span>}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums md:text-3xl">{value}</p>
    </motion.div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
